import { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import {
    getBuildingIdByUrlSlug,
    getBuildingDevice,
    HasUrlSlug,
    HasDeviceId,
} from 'selectors/buildingsSelectors';
import { getDeviceEvents, getDeviceEventsHasMore, getDeviceEventsError } from 'selectors/deviceEventsSelectors';
import { EventList } from 'ui/building/events/eventList';
import { makeCsvUrl, disposeCsvUrl } from 'ui/building/events/eventsExporter';
import { Dictionary, values } from 'lodash';
import { DeviceEvent } from 'shared/models';
import { useParams } from 'util/reactRouterHooks';
import { getDeviceEventsRequest } from 'actions/deviceEventsActions';

require('./deviceEventsPage.css');

export const DeviceEventsPage = () => {
    const { t } = useTranslation();
    const params = useParams<HasUrlSlug & HasDeviceId>();
    const [ exportUrl, setExportUrl ] = useState<string>();
    
    const { deviceId, urlSlug } = params;
    
    const buildingId = useSelector((state: State) => getBuildingIdByUrlSlug(state, params))!;

    const events = useSelector((state: State) => getDeviceEvents(state, params));
    // TODO: const isLoading = useSelector((state: State) => getDeviceEventsLoading(state, params));
    const olderThan = getIdOfOldestEvent(events);

    const device = useSelector((state: State) => getBuildingDevice(state, { ...params, buildingId }));
    const deviceNameById = useMemo(() =>
        device
            ? (id: string) => device.displayName || device.id
            : (id: string) => id
    , [ device ]);

    const safeDeviceName =
        device && (device.displayName || device.id).replace(/[^a-z0-9_+-]+/gi, '-');
    
    const hasMore = useSelector((state: State) => getDeviceEventsHasMore(state, params));
    const errorMessage = useSelector((state: State) => getDeviceEventsError(state));
    const exportFilename = `${urlSlug}_${safeDeviceName}_events.csv`;

    const dispatch = useDispatch();

    const handleClickShowMore = useCallback(() => {
        if (deviceId) {
            dispatch(getDeviceEventsRequest(deviceId, { olderThan }));
        }
    }, [ dispatch, deviceId, olderThan ]);

    // Request events when the device ID changes (also on page load)
    useEffect(() => {
        if (deviceId) {
            dispatch(getDeviceEventsRequest(deviceId));
        }
    }, [ dispatch, deviceId ]);

    // Create and dispose of the export URL
    useEffect(() => {
        let exportUrl: string | undefined;

        setExportUrl(_ => {
            exportUrl = makeCsvUrl(events, deviceNameById);
            return exportUrl;
        });

        return () => disposeCsvUrl(exportUrl);
    }, [ deviceId, setExportUrl, events, deviceNameById ]);

    return (
        <div id="device-events-page" className="page">
            <a
                href={ exportUrl || '#' }
                className={ `ui basic button ${exportUrl ? '' : 'disabled'}` }
                style={ { marginBottom: '2em' } }
                download={ exportUrl && exportFilename }
            >
                { t('COMMAND.EXPORT_CSV') }
            </a>
            <EventList
                events={ events }
                deviceNameById={ deviceNameById }
                onClickShowMore={ handleClickShowMore }
                hasMore={ hasMore }
                errorMessage={ errorMessage }
            />
        </div>
    );
};

function getIdOfOldestEvent(events: Dictionary<DeviceEvent> | null): number {
    if (!events) return 0;
    return values(events).map(e => e.id).sort().shift() || 0;
}
